import { ee } from '../../services/earth-engine';
import * as Metadata from '../../common/metadata';
import { getDate } from '../utils';
import { extractShoreline, linearGaussianFilter, removeShorelineNoise } from '../shoreline/extraction';

/*
 * Shore-S's reference-shoreline extraction, split in two steps:
 *
 *   1. `extractReferenceCandidate` reads the image and returns the raw
 *      coastline. Uses no transects, so it can run before the baseline exists.
 *   2. `cleanReferenceCandidate` uses the transects to pick the real
 *      coastline out of the raw one.
 *
 * `deriveShoreline` (`algorithms/shoreline/extraction.js`) does both at once.
 * This file composes the same shared functions, used as they are, so nothing
 * in `algorithms/shoreline/` had to change.
 */

/**
 * @constant REFERENCE_THRESHOLD
 * @description Land/water threshold for the reference extraction. Fixed, not configurable.
 * @memberof module:ShoreS
 */
export const REFERENCE_THRESHOLD = 0.01;

/**
 * @constant REFERENCE_EXTRACTION_SCALE
 * @description Sampling scale in meters, the same value the other flows use.
 * @memberof module:ShoreS
 */
export const REFERENCE_EXTRACTION_SCALE = 30;

// Reference image for `image.register`. Private to this module, so it is never
// mixed up with the one `extraction.js` keeps for the other flows.
let coRegistrationReference = null;

/**
 * @function resetCoRegistration
 * @description Clears the co-registration reference image, so each extraction starts from its
 * own first image instead of inheriting the previous run's.
 * **Called by:
 * @see {shoreSAnalysis/saga} - before extracting a new batch of candidates.
 * @memberof module:ShoreS
 * @returns {void}
 */
export const resetCoRegistration = () => {
  coRegistrationReference = null;
};

/**
 * @function extractReferenceCandidate
 * @description Step 1 of the reference extraction: reads one image and returns its raw coastline,
 * every segment found inside the ROI. Picking the right segment is step 2's job, which is what
 * lets this run before the baseline and the transects exist.
 * @param {Object} image - ee.Image for a single date.
 * @param {Object} roi - ee.Geometry the extraction is clipped to.
 * @param {Object} bands - The mission's band mapping (needs red/green/blue/nir).
 * @param {number} opticalResolution - Max pixel displacement for co-registration.
 * @see {@link cleanReferenceCandidate} - Step 2, which consumes this result.
 * **Called by:
 * @see {shoreSAnalysis/saga} - via `extractCandidatesInChunks`.
 * @memberof module:ShoreS
 * @returns {Object|null} ee.Feature with the raw geometry and `system:time_start`, or null when
 * the threshold rejected the image.
 */
export const extractReferenceCandidate = (image, roi, bands, opticalResolution) => {
  let workingImage = ee.Image(image);

  if (coRegistrationReference === null) {
    coRegistrationReference = workingImage.select(bands.red, bands.green, bands.blue, bands.nir);
  } else {
    workingImage = workingImage
      .select(bands.red, bands.green, bands.blue, bands.nir)
      .register(coRegistrationReference, opticalResolution);
  }

  const shoreline = extractShoreline(workingImage, roi, REFERENCE_EXTRACTION_SCALE, bands, () => REFERENCE_THRESHOLD);
  if (shoreline === null) return null;

  return ee.Feature(shoreline, {
    [Metadata.TIME_START]: ee.Date(getDate(workingImage)).format(null, 'UTC'),
  });
};

/**
 * @function cleanReferenceCandidate
 * @description Step 2 of the reference extraction: keeps the segment that crosses the most
 * transects and smooths it. Needs the transects, so it only runs once the user has configured them.
 * @param {Object} candidate - ee.Feature from `extractReferenceCandidate`, or an
 * `ee.Feature(geojson)` rebuilt from the cached one.
 * @param {Object} transects - ee.FeatureCollection or ee.List of transect lines.
 * @see {@link extractReferenceCandidate} - Step 1, which produces the candidate.
 * **Called by:
 * @see {shoreSAnalysis/saga} - via `cleanCandidatesInChunks`.
 * @memberof module:ShoreS
 * @returns {Object} ee.Feature with the cleaned LineString and `system:time_start`.
 */
export const cleanReferenceCandidate = (candidate, transects) => {
  const feature = ee.Feature(candidate);
  const transectsGeometry = ee.FeatureCollection(transects).geometry();

  const enhanced = removeShorelineNoise(feature.geometry(), transectsGeometry);
  const smoothen = linearGaussianFilter(enhanced.coordinates());

  return ee.Feature(ee.Geometry.LineString(smoothen), {
    [Metadata.TIME_START]: feature.get(Metadata.TIME_START),
  });
};

/**
 * @function buildExtractionKey
 * @description Fingerprints what the extraction was made from. A different string means the
 * cached coastlines no longer match and have to be extracted again. The baseline and the transect
 * settings are left out on purpose: they only affect step 2, so changing them reuses the cache.
 * @param {Object} inputs - The extraction inputs.
 * @param {Array} inputs.selectedImages - Dates chosen in ShoreSPeriodImageChooser.
 * @param {string} inputs.satelliteName - Name of the selected mission.
 * @param {string} inputs.crs - The model file's CRS.
 * @param {Array<number>} inputs.modelBBox - The model's bounding box.
 * **Called by:
 * @see {useShoreSReferenceExtraction} - to decide whether a cached run still applies.
 * @memberof module:ShoreS
 * @returns {string} The fingerprint of the extraction inputs.
 */
export const buildExtractionKey = ({ selectedImages, satelliteName, crs, modelBBox }) => {
  const images = (selectedImages || [])
    .map((image) => `${image.name}|${image.date}`)
    .sort()
    .join(',');

  return [images, satelliteName || '', crs || '', (modelBBox || []).join(',')].join('::');
};

/**
 * @function isDrawableLineGeometry
 * @description Checks whether a geometry is one of the only two types the next steps know how
 * to handle.
 * @param {Object} geometry - A GeoJSON geometry.
 * **Called by:
 * @see {keepUsableCandidates}
 * @memberof module:ShoreS
 * @returns {boolean} True for LineString and MultiLineString.
 */
const isDrawableLineGeometry = (geometry) =>
  geometry?.type === 'LineString' || geometry?.type === 'MultiLineString';

/**
 * @function keepUsableCandidates
 * @description Drops the results that came back as something other than a line, and reports how
 * many were discarded so the caller can warn the user.
 * @param {Array<Object>} features - Evaluated GeoJSON features, nulls included.
 * **Called by:
 * @see {shoreSAnalysis/saga} - after evaluating a chunk of candidates.
 * @memberof module:ShoreS
 * @returns {Object} `{ features, discarded }`, the usable features and the discarded count.
 */
export const keepUsableCandidates = (features) => {
  const usable = (features || []).filter((feature) => feature && isDrawableLineGeometry(feature.geometry));
  return { features: usable, discarded: (features || []).length - usable.length };
};

const DISPLAY_SMOOTHING_SAMPLES = 3;

/**
 * @function gaussianWeights
 * @description Builds the same normalized weights `gaussianKernel` produces, computed here on
 * the client instead of on the server.
 * @param {number} samples - Kernel width, in points.
 * @param {number} [mean] - Center of the curve.
 * @param {number} [sd] - Standard deviation of the curve.
 * **Called by:
 * @see {smoothCoordinates} - via the precomputed DISPLAY_SMOOTHING_WEIGHTS.
 * @memberof module:ShoreS
 * @returns {Array<number>} Weights summing to 1.
 */
const gaussianWeights = (samples, mean = 0, sd = 1) => {
  const half = Math.floor(samples / 2);
  const curve = [];

  for (let x = -half; x <= half; x += 1) {
    curve.push(Math.exp(-((x - mean) ** 2) / (2 * sd ** 2)) / (sd * Math.sqrt(2 * Math.PI)));
  }

  const total = curve.reduce((sum, value) => sum + value, 0);
  return curve.map((value) => value / total);
};

const DISPLAY_SMOOTHING_WEIGHTS = gaussianWeights(DISPLAY_SMOOTHING_SAMPLES);

/**
 * @function smoothCoordinates
 * @description JS counterpart of `linearGaussianFilter`, with the same kernel and the same
 * "keep the first and last points" behavior. The raw coastline follows the pixel grid
 * `reduceToVectors` produced, so every corner is a right angle. Step 2 smooths it on the server;
 * this does the same on the drawn copy only, leaving the cached geometry untouched.
 * @param {Array<Array<number>>} coordinates - [[lng, lat], ...] positions.
 * **Called by:
 * @see {toDisplayFeatures} - when the `smooth` option is on.
 * @memberof module:ShoreS
 * @returns {Array<Array<number>>} The smoothed positions.
 */
const smoothCoordinates = (coordinates) => {
  if (coordinates.length <= DISPLAY_SMOOTHING_SAMPLES) return coordinates;

  const smoothed = [coordinates[0]];

  for (let i = 0; i <= coordinates.length - DISPLAY_SMOOTHING_SAMPLES; i += 1) {
    let lng = 0;
    let lat = 0;
    DISPLAY_SMOOTHING_WEIGHTS.forEach((weight, k) => {
      lng += coordinates[i + k][0] * weight;
      lat += coordinates[i + k][1] * weight;
    });
    smoothed.push([lng, lat]);
  }

  smoothed.push(coordinates[coordinates.length - 1]);
  return smoothed;
};

/**
 * @function toDisplayFeatures
 * @description Splits MultiLineString features into one LineString per segment, for drawing:
 * `addShape` (`common/map.js`) only draws LineString and Polygon.
 * @param {Array<Object>} features - GeoJSON features (LineString or MultiLineString).
 * @param {Object} [options] - Display options.
 * @param {boolean} [options.smooth] - Only for the raw coastlines. The cleaned ones were already
 * smoothed by step 2, and smoothing them again would draw a line that no longer matches the one
 * the distances were measured on.
 * **Called by:
 * @see {shoreSAnalysis/saga} - before handing the coastlines to `drawFeatures`.
 * @memberof module:ShoreS
 * @returns {Array<Object>} LineString features, with `segment` added to the properties.
 */
export const toDisplayFeatures = (features, { smooth = false } = {}) => {
  const display = [];

  const push = (coordinates, properties, segment) => {
    // A LineString needs at least two positions.
    if (coordinates.length < 2) return;
    display.push({
      type: 'Feature',
      geometry: { type: 'LineString', coordinates: smooth ? smoothCoordinates(coordinates) : coordinates },
      properties: { ...properties, segment },
    });
  };

  (features || []).forEach(({ geometry, properties }) => {
    if (geometry?.type === 'LineString') {
      push(geometry.coordinates, properties, 0);
      return;
    }

    if (geometry?.type !== 'MultiLineString') return;

    geometry.coordinates.forEach((coordinates, segment) => push(coordinates, properties, segment));
  });

  return display;
};
